/**
 * Maps real `public.surveys` rows onto the display shapes the frontend
 * uses. Ground truth for column names is the generated Supabase types in
 * `src/types/database.types.ts`.
 *
 * The surveys table has NO `name`, `title`, `completed_at`, `survey_type`,
 * `user_id`, `owner_id` or analysis-result columns. The conventions
 * (established with the SurveyDetailPage fix) are:
 *   - display name: `metadata.name` (jsonb) → joined parcel name →
 *     translated "Untitled survey" fallback (callers pass the t() result)
 *   - completion date: `updated_at` when the status says complete
 *   - survey "type": derived from `modules`
 *   - analysis metrics: the `analysis_results` table (`result_summary`
 *     jsonb keyed by survey_id + module), never surveys columns
 *   - ownership: `requested_by` (FK → profiles), never `user_id`/`owner_id`
 */

import type { Json, Tables } from '@/types/database.types';

/**
 * The only `surveys` columns frontend queries may select (joins like
 * `parcels(name)` are appended at the call site). Every name is verified
 * against `Database['public']['Tables']['surveys']['Row']` via the
 * `SurveyCoreRow` Pick below — adding a column that doesn't exist on the
 * generated Row type is a compile error, which is what stops the
 * "phantom column → PostgREST 400 → widget loads forever" bug class.
 */
export const SURVEY_CORE_COLUMNS =
  'id, status, priority, modules, created_at, updated_at, parcel_id, metadata';

export type SurveyCoreRow = Pick<
  Tables<'surveys'>,
  | 'id'
  | 'status'
  | 'priority'
  | 'modules'
  | 'created_at'
  | 'updated_at'
  | 'parcel_id'
  | 'metadata'
>;

/**
 * The app convention is `status = 'complete'` (see `lib/demoData.ts`
 * SurveyStatus), but a few older write paths used `'completed'` — accept
 * both when filtering/deriving so neither spelling hides real surveys.
 */
export const SURVEY_COMPLETE_STATUSES = ['complete', 'completed'] as const;

export function isSurveyComplete(status: string | null | undefined): boolean {
  return (SURVEY_COMPLETE_STATUSES as readonly string[]).includes(status ?? '');
}

/**
 * Display name chain: `metadata.name` → joined parcel name → fallback.
 * The fallback must be the already-translated "Untitled survey" string
 * (callers pass `t('owner.surveys.untitled')`).
 */
export function surveyDisplayName(
  metadata: Json | null | undefined,
  parcelName: string | null | undefined,
  fallback: string,
): string {
  const meta = (metadata ?? {}) as Record<string, unknown>;
  if (typeof meta.name === 'string' && meta.name.trim()) return meta.name;
  if (typeof parcelName === 'string' && parcelName.trim()) return parcelName;
  return fallback;
}

/**
 * surveys has no `completed_at` column — the completion date is
 * `updated_at` when the status says the survey is complete, else null.
 */
export function surveyCompletedAt(
  row: Pick<SurveyCoreRow, 'status' | 'updated_at'>,
): string | null {
  return isSurveyComplete(row.status) ? (row.updated_at ?? null) : null;
}

/**
 * Analysis metrics live in `analysis_results.result_summary` (jsonb, one
 * row per survey_id + module) — never on surveys. This merges the
 * per-module summaries into one flat record for report builders, skipping
 * null/non-object summaries.
 */
export function mergeResultSummaries(
  rows:
    | ReadonlyArray<{ result_summary: Json | null } | null | undefined>
    | null
    | undefined,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {};
  for (const row of rows ?? []) {
    const summary = row?.result_summary;
    if (summary && typeof summary === 'object' && !Array.isArray(summary)) {
      Object.assign(merged, summary as Record<string, unknown>);
    }
  }
  return merged;
}
